/*
 * amplify UI Web Components の shadow DOM 内に直接 CSS を注入するユーティリティ
 *
 * 背景:
 *   amplify-hint 等のコンポーネントは shadow DOM 内部にデフォルトの色やサイズを
 *   ハードコードしており、外部 CSS（halmek-custom.css）からは届かない。
 *   shadow DOM 越しに inherit する CSS カスタムプロパティでも、対象コンポーネントが
 *   :host で同名変数をセットしている場合は上書きできない。
 *
 *   そのため、対象 shadow DOM の中に直接 <style> 要素を挿入し、内部クラス
 *   （例: .hint）に対するルールで強制的に上書きする。
 *
 * 注入する CSS の管理:
 *   ルール本体は /assets/css/halmek-shadow.css（S3 + CloudFront 配信、Amplify 管理外）
 *   から fetch する。これにより React コードを変更せずに、CSS ファイルの差し替えだけで
 *   shadow DOM 内の見た目を調整できる。
 *
 *   halmek-shadow.css 内では `@shadow:タグ名` ～ `@shadow:end` のコメントブロックで
 *   コンポーネントごとのルールを区切る:
 *
 *     /* @shadow:amplify-hint *\/
 *     .hint { color: rgba(0,0,0,0.87) !important; }
 *     /* @shadow:end *\/
 *
 * リスク・メンテ事項:
 *   - amplify が内部で使うクラス名（.hint 等）に依存している。
 *     amplify をバージョンアップ（特に v2 系）した場合、クラス名や構造の変更で
 *     注入が効かなくなる可能性がある。amplify-ui 1.x はメンテナンスモードのため
 *     変わらない見込み。
 *   - lazy-load で後から amplify コンポーネントが mount されるケースに対応するため
 *     MutationObserver + 短時間 polling で監視している。
 *   - !important が必要な理由: amplify は constructed stylesheet (adoptedStyleSheets)
 *     を使っており、通常の <style> 要素のルールはこれに勝てない。!important なら
 *     cascade order に関わらず勝てる。
 */

const SHADOW_CSS_URL = '/assets/css/halmek-shadow.css';
const INJECTED_MARKER = 'data-halmek-injected';

/* halmek-shadow.css のテキストから「タグ名 → CSS」マップを作る
 * フォーマット:
 *   /* @shadow:タグ名 *\/  ... CSS ...  /* @shadow:end *\/
 * 同じタグ名のブロックが複数あった場合は連結する */
function parseShadowStyles(cssText) {
	const result = {};
	const re = /\/\*\s*@shadow:([\w-]+)\s*\*\/([\s\S]*?)\/\*\s*@shadow:end\s*\*\//g;
	let m;
	while ((m = re.exec(cssText)) !== null) {
		const tagName = m[1];
		const body = m[2];
		result[tagName] = (result[tagName] || '') + body;
	}
	return result;
}

/* shadow DOM を再帰的に潜って指定タグ名の要素を全て返す */
function deepQueryAll(tagName, root = document) {
	const result = [];
	if (root.querySelectorAll) {
		root.querySelectorAll(tagName).forEach(el => result.push(el));
	}
	const all = root.querySelectorAll ? root.querySelectorAll('*') : [];
	for (const el of all) {
		if (el.shadowRoot) {
			result.push(...deepQueryAll(tagName, el.shadowRoot));
		}
	}
	return result;
}

/* 単一の shadow DOM に <style> を注入（重複防止付き） */
function injectStyleInto(shadowRoot, css) {
	if (!shadowRoot) return;
	if (shadowRoot.querySelector(`style[${INJECTED_MARKER}]`)) return;
	const style = document.createElement('style');
	style.setAttribute(INJECTED_MARKER, 'true');
	style.textContent = css;
	shadowRoot.appendChild(style);
}

/* 全対象コンポーネントを処理 */
function processAll(shadowStyles) {
	Object.entries(shadowStyles).forEach(([tagName, css]) => {
		const elements = deepQueryAll(tagName);
		elements.forEach(el => injectStyleInto(el.shadowRoot, css));
	});
}

/* 初回実行 + 動的 mount 監視
 * shadow DOM 内部の mutation は document.body の MutationObserver では検知できないため、
 * polling で確実に追従する。注入は idempotent（INJECTED_MARKER で重複防止）なので、
 * 何度呼ばれても問題ない。
 * 50ms 間隔で 5 秒間（最大 100 回）polling する。これにより amplify が mount してから
 * 注入されるまでの遅延が最大 50ms 程度になり、見た目のチラつきが認識しづらくなる。
 * 5 秒経過後は polling 停止（合計負荷は軽微）。
 *
 * fetch は非同期だが、発火後すぐ ready になるとは限らないので、
 * fetch 解決を待たずに polling を始め、解決後にスタイルマップを更新する */
export function injectAmplifyShadowStyles() {
	let shadowStyles = {};

	const cssReady = fetch(SHADOW_CSS_URL, { cache: 'no-cache' })
		.then(res => {
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			return res.text();
		})
		.then(text => {
			shadowStyles = parseShadowStyles(text);
		})
		.catch(err => {
			// shadow DOM 内のスタイルが当たらなくなるだけで他の動作には影響しない
			console.warn('[halmek] failed to load shadow CSS:', err);
		});

	const tick = () => processAll(shadowStyles);
	cssReady.then(tick);

	const observer = new MutationObserver(tick);
	observer.observe(document.body, { subtree: true, childList: true });
	const interval = setInterval(tick, 50);
	setTimeout(() => clearInterval(interval), 5000);
	return { observer, interval };
}
